import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Instagram } from "lucide-react";
import { toast } from "sonner";
import useSeo from "../hooks/useSeo";
import SectionHeader from "../components/common/SectionHeader";
import ProductGrid from "../components/product/ProductGrid";
import {
  COLLECTIONS,
  getFeaturedProducts,
  getNewArrivals,
} from "../data/products";
import { storeConfig } from "../config/store.config";

const HERO_IMG =
  "https://images.unsplash.com/photo-1532332248682-206cc786359f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const BANNER_IMG =
  "https://images.unsplash.com/photo-1618924250113-e162305ac8cd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const SOCIAL = [
  "https://images.unsplash.com/photo-1635650804483-2a77a8c9e728?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1721637635502-b0abaaa75edb?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1647547844452-3abd77700ac1?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1721637686340-de9f8cebda5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.pexels.com/photos/19583558/pexels-photo-19583558.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=500",
  "https://images.pexels.com/photos/28484979/pexels-photo-28484979.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=500",
];

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const onSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setEmail("");
    toast("Thanks! Newsletter isn't live yet — we'll be in touch soon.");
  };
  return (
    <section className="bg-ink py-20 text-paper">
      <div className="container-9one max-w-2xl text-center">
        <p className="label-mono text-brand">Stay in the loop</p>
        <h2 className="mt-3 font-display text-4xl uppercase tracking-tight sm:text-5xl">
          Be first to know
        </h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-paper/60">
          Sign up for drops and new arrivals. No spam, just the good stuff.
        </p>
        <form onSubmit={onSubmit} className="mx-auto mt-8 flex max-w-md gap-2" data-testid="newsletter-form">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            aria-label="Email address"
            data-testid="newsletter-email"
            className="w-full border border-white/25 bg-transparent px-4 py-3.5 font-mono text-sm text-paper placeholder:text-paper/40 focus:border-brand focus:outline-none"
          />
          <button type="submit" className="btn-invert shrink-0" data-testid="newsletter-submit">
            Join
          </button>
        </form>
      </div>
    </section>
  );
};

const Home = () => {
  useSeo({
    title: null,
    description:
      "9'One Store — premium contemporary streetwear and essential T-shirts. Shop oversized, minimal and graphic tees. Order easily via WhatsApp.",
    path: "/",
  });

  const featured = getFeaturedProducts().slice(0, 8);
  const arrivals = getNewArrivals().slice(0, 4);

  return (
    <div className="animate-fade-in">
      {/* ---------------- HERO ---------------- */}
      <section className="border-b border-ink" data-testid="hero-section">
        <div className="grid lg:grid-cols-2">
          <div className="flex flex-col justify-center px-4 py-16 sm:px-8 lg:px-12 lg:py-0">
            <p className="label-mono text-ink/50">{storeConfig.tagline}</p>
            <h1 className="mt-5 display-h1 text-[15vw] leading-[0.85] sm:text-7xl lg:text-8xl">
              9'One
              <br />
              <span className="text-brand-dark">Store</span>
            </h1>
            <p className="mt-6 max-w-md text-base text-ink/70">
              Elevated everyday tees and statement streetwear. Considered fits,
              premium fabrics, monochrome soul.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/shop" className="btn-solid" data-testid="hero-shop-now">
                Shop Now <ArrowRight size={16} />
              </Link>
              <Link to="/collections" className="label-mono link-underline pb-1">
                View Collections
              </Link>
            </div>
          </div>
          <div className="relative min-h-[60vh] overflow-hidden bg-cream lg:min-h-[80vh]">
            <img
              src={HERO_IMG}
              alt="9'One Store streetwear editorial"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* ---------------- SHOP BY STYLE ---------------- */}
      <section className="container-9one py-20" data-testid="shop-by-style">
        <SectionHeader eyebrow="Find your fit" title="Shop by Style" />
        <div className="grid grid-cols-2 gap-px bg-ink lg:grid-cols-4">
          {COLLECTIONS.map((c) => (
            <Link
              key={c.slug}
              to={`/shop?collection=${c.slug}`}
              className="group relative aspect-[3/4] overflow-hidden bg-cream"
              data-testid={`style-card-${c.slug}`}
            >
              <img
                src={c.image}
                alt={c.name}
                loading="lazy"
                className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
              <div className="absolute bottom-0 left-0 p-5 text-paper">
                <h3 className="font-display text-3xl uppercase tracking-tight">{c.name}</h3>
                <p className="mt-1 flex items-center gap-1 label-mono opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  Explore <ArrowUpRight size={14} />
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ---------------- NEW ARRIVALS ---------------- */}
      <section className="container-9one py-8 pb-20" data-testid="new-arrivals">
        <SectionHeader
          eyebrow="Fresh in"
          title="New Arrivals"
          action={
            <Link to="/shop" className="label-mono link-underline hidden pb-1 sm:inline-flex">
              View All
            </Link>
          }
        />
        <ProductGrid products={arrivals} />
      </section>

      {/* ---------------- FEATURED COLLECTION BANNER ---------------- */}
      <section className="relative" data-testid="featured-collection">
        <div className="relative min-h-[70vh] overflow-hidden">
          <img src={BANNER_IMG} alt="Featured collection" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/45" />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center text-paper">
            <p className="label-mono text-brand">The Monochrome Edit</p>
            <h2 className="mt-4 font-display text-5xl uppercase leading-none tracking-tight sm:text-7xl">
              Built for the
              <br />
              everyday street
            </h2>
            <Link to="/collections" className="btn-invert mt-8" data-testid="featured-collection-cta">
              Explore Collection <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------- FEATURED PRODUCTS ---------------- */}
      <section className="container-9one py-20" data-testid="featured-products">
        <SectionHeader eyebrow="Handpicked" title="Featured" />
        <ProductGrid products={featured} />
      </section>

      {/* ---------------- BRAND SECTION ---------------- */}
      <section className="bg-cream py-24" data-testid="brand-section">
        <div className="container-9one max-w-3xl text-center">
          <p className="label-mono text-ink/50">The Brand</p>
          <h2 className="mt-4 font-display text-4xl uppercase leading-none tracking-tight sm:text-6xl">
            9'One Store
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-ink/70">
            9'One Store is a contemporary fashion label focused on premium
            everyday t-shirts and streetwear essentials. Clean silhouettes,
            considered details, and a monochrome aesthetic — made to wear on
            repeat. <span className="text-ink/40">(Editable brand copy.)</span>
          </p>
          <Link to="/about" className="btn-outline mt-9" data-testid="brand-about-cta">
            Our Story
          </Link>
        </div>
      </section>

      {/* ---------------- SOCIAL SECTION ---------------- */}
      <section className="container-9one py-20" data-testid="social-section">
        <SectionHeader
          eyebrow="Community"
          title="On the Street"
          action={
            storeConfig.instagramUrl ? (
              <a
                href={storeConfig.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="label-mono link-underline hidden items-center gap-1 pb-1 sm:inline-flex"
              >
                <Instagram size={15} /> Follow
              </a>
            ) : (
              <span className="label-mono hidden items-center gap-1 text-ink/40 sm:inline-flex">
                <Instagram size={15} /> Coming soon
              </span>
            )
          }
        />
        <div className="grid grid-cols-2 gap-px bg-ink sm:grid-cols-3 lg:grid-cols-6">
          {SOCIAL.map((src, i) => (
            <a
              key={i}
              href={storeConfig.instagramUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative aspect-square overflow-hidden bg-cream"
              data-testid={`social-tile-${i}`}
            >
              <img
                src={src}
                alt="9'One community"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-ink/0 transition-colors duration-300 group-hover:bg-ink/40">
                <Instagram size={22} className="text-paper opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ---------------- NEWSLETTER ---------------- */}
      <Newsletter />
    </div>
  );
};

export default Home;
